import { Link } from "react-router-dom";
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper } from '@mui/material';

import { useTrans } from '~/hooks/useTrans';
import { getLink } from "~/providers/route";
import { IProjectBillingHistory } from "~/providers/interfaces";
import Loader from '~/components/Loader';
import ButtonLink from '~/components/ButtonLink';
import { classNames, formatBillingHistoryRange } from "~/utils/utils";
import { IUrlParams } from "~/interfaces";
import { useLang } from "~/hooks/useLang";

interface IBillingHistoryTable {
	loading: boolean;
	isSOS: boolean;
	items: Array<IProjectBillingHistory>;
	isInternal: boolean;
	params: IUrlParams;
	onSwitchDebt: (billing: IProjectBillingHistory) => void;
}

export default function BillingHistoryTable({
	loading = true,
	isSOS = false,
	items = [],
	isInternal = false,
	params = null,
	onSwitchDebt,
}: IBillingHistoryTable) {
	const { formatCredits, formatPennyPrice, trans, transBillingHistoryState } = useTrans();
	const { isCzech } = useLang();
	const HEADERS: Array<{
		trans: string;
		center?: boolean;
	}> = [
		{ trans: "billingHistoryTable.range" },
		{ trans: "billingHistoryTable.state", center: true },
		{ trans: "projectDashboardPage.consumption.consumed", center: true },
		{ trans: "billingHistoryTable.freeCredits", center: true },
		{ trans: "billingHistoryTable.paidCredits", center: true },
		{ trans: "billingHistoryTable.amountTotal", center: true },
		{ trans: "" },
	];

	// datum spotreby bude vzdy od prvniho v mesici
	function getFromDate(fromDate: string) {
		const parts = fromDate.split("-");

		/* eslint-disable-next-line */
		if (parts.length === 3) {
			parts[2] = "01";

			return parts.join("-");
		}

		return fromDate;
	}

	function showSwitchDebtButton(billing: IProjectBillingHistory): boolean {
		return isSOS
			&& (billing.transactionState === "completed" || billing.transactionState === "partial")
			&& billing.amountTotal > 0;
	}

	function isDebt(billing: IProjectBillingHistory): boolean {
		return billing.transactionState === "partial";
	}

	return <TableContainer component={Paper}>
		<Table sx={{ minWidth: 650 }} aria-label="table">
			<TableHead>
				<TableRow>
					{ HEADERS.map(header => <TableCell sx={{ fontWeight: "bold", ...header.center ? { textAlign: "center" } : {} }} key={header.trans}>
						{ header.trans && trans(header.trans) }
					</TableCell>) }
				</TableRow>
			</TableHead>
			<TableBody>
				{ items.map(consumption => (<TableRow key={consumption.id}>
					<TableCell>
						{ formatBillingHistoryRange(consumption, isCzech) }
					</TableCell>
					<TableCell className={classNames(["text-align-center", isDebt(consumption) ? "red-text-color" : ""])}>
						{ transBillingHistoryState(consumption) }
					</TableCell>
					<TableCell className="text-align-center">
						{ formatCredits(consumption.totalConsumedCredits) }
					</TableCell>
					<TableCell className="text-align-center">
						{ formatCredits(consumption.creditsFreeOfCharge) }
					</TableCell>
					<TableCell className="text-align-center">
						{ formatCredits(consumption.creditsCharged) }
					</TableCell>
					<TableCell className={classNames(["text-align-center", isDebt(consumption) ? "red-text-color" : ""])}>
						{ formatPennyPrice(consumption.amountTotal) }
					</TableCell>
					<TableCell className="text-align-center">
						<Link to={getLink("project-consumption-detail", isInternal, { projectId: params.projectId, fromDate: getFromDate(consumption.billingPeriodFrom), tillDate: consumption.billingPeriodTill })} style={{ fontSize: "var(--default-font-size)" }}>
							{ trans("menu.consumptionDetail") }
						</Link>
						{ showSwitchDebtButton(consumption) && <>
							<span style={{ display: "inline-block", width: "var(--margin-small)" }} />
							<ButtonLink onClick={() => onSwitchDebt(consumption)} title={ trans("billingHistoryTable.switchDebt") } />
						</> }
					</TableCell>
				</TableRow>)) }
				{ items.length === 0 && <TableRow>
					<TableCell colSpan={HEADERS.length} className="text-align-center">
						{ loading && <Loader msg={trans("loadingData")} /> }
						{ !loading && trans("emptyTable") }
					</TableCell>
				</TableRow> }
			</TableBody>
		</Table>
	</TableContainer>;
}
